import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Box,
  Chip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getPatientScans, ScanListItem } from "../api/patients";

interface Props {
  patientId: string;
}

function scanPartChip(scanPart?: string | null) {
  switch (scanPart) {
    case "unified":
      return <Chip label="Единый тест" size="small" color="primary" />;
    case "part1":
      return <Chip label="Part 1" size="small" variant="outlined" />;
    case "part2":
      return <Chip label="Part 2 — MST" size="small" variant="outlined" />;
    case "part3":
      return <Chip label="Part 3 — Саккады" size="small" variant="outlined" />;
    default:
      return null;
  }
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("ru-RU", { timeZone: "Asia/Almaty" });
}

export default function ScanHistoryList({ patientId }: Props) {
  const [scans, setScans] = useState<ScanListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getPatientScans(patientId)
      .then((res) => setScans(res.items))
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <CircularProgress size={24} />;

  if (scans.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Сканы не найдены
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {scans.map((scan, idx) => {
        const isUnified = scan.scan_part === "unified";
        const content = (
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {scanPartChip(scan.scan_part)}
                <Typography variant="body2" component="span">
                  {scan.created_at ? formatDateTime(scan.created_at) : "Дата неизвестна"}
                </Typography>
              </Box>
            }
            secondary={
              <>
                {scan.user && `Оператор: ${scan.user.email}`}
                {scan.language && ` | Язык: ${scan.language.toUpperCase()}`}
              </>
            }
          />
        );

        // Результаты (аудио/транскрипции/MST) есть только у единого теста —
        // такие строки кликабельны и ведут в кабинет результатов
        return isUnified ? (
          <ListItem key={scan._id || idx} divider disablePadding>
            <ListItemButton onClick={() => navigate(`/patient/${patientId}/scan/${scan._id}`)}>
              {content}
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem key={scan._id || idx} divider>
            {content}
          </ListItem>
        );
      })}
    </List>
  );
}
